import { useState, useEffect, useCallback } from "react";
import { Link, useLocation, useNavigate, useParams } from "react-router-dom";
import { Plus } from "lucide-react";
import { KanbanBoard } from "./KanbanBoard";
import { ChamadoModal } from "./ChamadoModal";
import { DashboardFiltersComponent } from "./DashboardFilters";
import { BotoesExportacao } from "../../../components/ui/BotoesExportacao";
import { ErrorAlert } from "../../../components/ui/ErrorAlert";
import { KanbanSkeleton, StatsSkeleton, FiltersSkeleton } from "../../../components/ui/Skeleton";
import { useChamadoById } from "../hooks/useChamados";
import { useDashboardData } from "../hooks/useDashboardData";
import { useErrorHandler } from "../../../hooks/useErrorHandler";
import { useKanbanData } from "../../../hooks/useKanbanData";
import { exportarQuadro } from "../gestao/exportarQuadro";
import { usePodeAbrirChamado } from "../gestao/usePodeAbrirChamado";
import type { ChamadoKanban } from "../../../types/api";

const Dashboard = () => {
  const navigate = useNavigate();
  const podeAbrir = usePodeAbrirChamado();
  const location = useLocation();

  // O quadro responde em "/" e em "/kanban". O detalhe abre sobre ele, e
  // fechar precisa voltar para a base pela qual a pessoa entrou, senão o
  // caminho muda sozinho embaixo dela.
  const rotaBase = location.pathname.startsWith("/kanban") ? "/kanban" : "/";
  const { id: idFromUrl } = useParams<{ id: string }>();
  const [selectedChamadoId, setSelectedChamadoId] = useState<number | null>(
    null,
  );

  // Sincroniza o ID da URL com o state que abre o modal
  useEffect(() => {
    if (!idFromUrl) {
      setSelectedChamadoId(null);
      return;
    }
    const parsed = Number(idFromUrl);
    setSelectedChamadoId(Number.isFinite(parsed) && parsed > 0 ? parsed : null);
  }, [idFromUrl]);

  const handleChamadoClick = useCallback(
    (id: number) => {
      navigate(`${rotaBase === "/kanban" ? "/kanban" : "/chamado"}/${id}`);
    },
    [navigate, rotaBase],
  );

  const handleCloseModal = useCallback(() => {
    navigate(rotaBase);
  }, [navigate, rotaBase]);
  const {
    handleError,
    hasError,
    errorTitle,
    errorMessage,
    clearError,
    setRetryFn,
  } = useErrorHandler();

  // Usando hook integrado de dados e filtros
  const {
    chamados,
    isLoading,
    error,
    refetch,
    isFetching,
    filters,
    updateFilters,
    clearFilters,
    hasActiveFilters,
    filterOptions,
    activeFiltersCount,
    filteredCount,
    dataUpdatedAt,
  } = useDashboardData();

  const { data: chamadoDetalhes, isLoading: isLoadingDetalhes } =
    useChamadoById(selectedChamadoId);

  // Stats baseadas nos dados filtrados
  const { stats } = useKanbanData(chamados as any[]);

  const handleExport = useCallback((formato: "pdf" | "excel") => {
    // Manda os ids visíveis: o arquivo passa a corresponder ao que está na
    // tela depois dos filtros. Sem eles o servidor exportaria o mês inteiro.
    const ids = (chamados as ChamadoKanban[]).map((c) => c.id);

    const agora = new Date();
    let ano = agora.getFullYear();
    let mes = agora.getMonth() + 1;
    if (filters.mesApuracao) {
      const [a, m] = filters.mesApuracao.split("-").map(Number);
      if (Number.isFinite(a) && Number.isFinite(m)) {
        ano = a;
        mes = m;
      }
    }

    return exportarQuadro(mes, ano, ids, formato);
  }, [chamados, filters.mesApuracao]);

  // Configurar função de retry
  useEffect(() => {
    if (error) {
      handleError(error, { action: "fetch_chamados_kanban" });
      setRetryFn(() => refetch);
    } else {
      clearError();
    }
  }, [error, handleError, clearError, setRetryFn, refetch]);

  if (isLoading) {
    return (
      <div className="space-y-8">
        {/* Header do Dashboard */}
        <div className="text-center lg:text-left">
          <h1 className="text-4xl font-bold bg-gradient-to-r from-blue-800 via-blue-600 to-blue-500 bg-clip-text text-transparent">
            Dashboard de Chamados
          </h1>
          <p className="text-gray-600 mt-2 text-lg">
            Carregando dados dos chamados...
          </p>
        </div>

        {/* Filtros skeleton */}
        <FiltersSkeleton />

        {/* Stats cards skeleton */}
        <StatsSkeleton />

        {/* Kanban Board skeleton */}
        <div className="bg-white/40 backdrop-blur-sm rounded-2xl p-6 border border-gray-200/50">
          <KanbanSkeleton />
        </div>
      </div>
    );
  }

  if (hasError) {
    return (
      <div className="flex items-center justify-center min-h-screen p-4">
        <div className="max-w-md w-full">
          <ErrorAlert
            title={errorTitle}
            message={errorMessage}
            onRetry={() => refetch()}
            onClose={clearError}
            variant="error"
          />
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {/* Cabeçalho enxuto (05/10/2026): título menor, sem subtítulo, e a exportação à direita;
          logo abaixo, os filtros e os números do mês. */}
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex flex-wrap items-center gap-x-4 gap-y-2">
          <h1 className="text-xl font-bold bg-gradient-to-r from-blue-800 via-blue-600 to-blue-500 bg-clip-text text-transparent sm:text-2xl">
            Dashboard de Chamados
          </h1>
          <div className="flex items-center gap-2 text-sm">
            {hasActiveFilters && (
              <span className="rounded-md bg-blue-100 px-2 py-1 text-blue-800">
                {filteredCount} de {chamados?.length || 0}
                {activeFiltersCount > 0 &&
                  ` · ${activeFiltersCount} filtro${activeFiltersCount > 1 ? "s" : ""}`}
              </span>
            )}
          </div>
        </div>
        <div className="flex items-center gap-2">
          <BotoesExportacao
            aoExportar={handleExport}
            desabilitado={(chamados as ChamadoKanban[]).length === 0}
          />
          {/* Abrir chamado também daqui (06/10/2026), pela mesma regra da lista. */}
          {podeAbrir && (
            <Link
              to="/chamados/novo"
              state={{ de: "kanban" }}
              className="inline-flex h-9 items-center gap-2 rounded-lg bg-gradient-to-r from-blue-800 to-blue-600 px-4 text-sm font-medium text-white shadow-sm transition-colors hover:from-blue-900 hover:to-blue-700"
            >
              <Plus className="h-4 w-4" />
              Abrir chamado
            </Link>
          )}
        </div>
      </div>

      {/* Filtros do Dashboard */}
      <DashboardFiltersComponent
        filters={filters}
        onFiltersChange={updateFilters}
        onClearFilters={clearFilters}
        hasActiveFilters={hasActiveFilters}
        activeFiltersCount={activeFiltersCount}
        severidadeOptions={filterOptions.severidade}
        statusOptions={filterOptions.status}
        mesApuracaoOptions={filterOptions.mesApuracao}
        produtoOptions={filterOptions.produto}
      />

      {/* Stats cards */}
      {/* No celular os três números cabem numa linha (06/10/2026). */}
      <div className="grid grid-cols-3 gap-2 mb-2 sm:gap-4">
        <div className="bg-white rounded-xl p-3 border border-gray-200 sm:p-5">
          <p className="text-[10px] font-medium text-gray-400 uppercase tracking-wide sm:text-xs">Total</p>
          <p className="text-xl font-semibold text-gray-900 mt-1 tabular-nums sm:text-2xl">{stats.total}</p>
        </div>
        <div className="bg-white rounded-xl p-3 border border-gray-200 sm:p-5">
          <p className="text-[10px] font-medium text-gray-400 uppercase tracking-wide sm:text-xs">Em andamento</p>
          <p className="text-xl font-semibold text-yellow-600 mt-1 tabular-nums sm:text-2xl">{stats.emAndamento}</p>
        </div>
        <div className="bg-white rounded-xl p-3 border border-gray-200 sm:p-5">
          <p className="text-[10px] font-medium text-gray-400 uppercase tracking-wide sm:text-xs">Concluídos</p>
          <p className="text-xl font-semibold text-green-600 mt-1 tabular-nums sm:text-2xl">{stats.concluidos}</p>
        </div>
      </div>

      {/* Kanban Board */}
      <div className="bg-white/40 backdrop-blur-sm rounded-2xl p-3 border border-gray-200/50 sm:p-6">
        <KanbanBoard
          chamados={chamados as any[]}
          onChamadoClick={handleChamadoClick}
          onRefresh={() => refetch()}
          isRefreshing={isFetching && !isLoading}
          dataUpdatedAt={dataUpdatedAt}
          selectedChamadoId={selectedChamadoId}
          isLoadingDetails={isLoadingDetalhes}
        />
      </div>

      {/* Modal de Detalhes - Renderizado no nível da página */}
      <ChamadoModal
        isOpen={!!selectedChamadoId}
        onClose={handleCloseModal}
        chamado={chamadoDetalhes || null}
        isLoading={!!selectedChamadoId && isLoadingDetalhes}
      />
    </div>
  );
};

export default Dashboard;
